import { usePage } from "@inertiajs/react"
import { createConsumer } from "@rails/actioncable"
import { useEffect, useRef, useState } from "react"

import { memberNotificationsUnreadCountPath } from "@/routes"

/**
 * Returns the live unread notification count.
 * - Seeds from Inertia shared data (instant, no extra request)
 * - Performs one fetch on mount for an up-to-date seed value
 * - Subscribes to NotificationsChannel via ActionCable for real-time updates
 * - Resets to 0 immediately when the user visits /app/notifications (via Inertia shared data)
 */
export function useUnreadNotifications() {
  const { auth } = usePage().props as {
    auth: { user?: { id?: number; unread_notifications_count?: number } }
  }

  const [count, setCount] = useState<number>(
    auth?.user?.unread_notifications_count ?? 0,
  )

  // Keep count in sync whenever Inertia shared data changes (e.g. after navigation to /notifications)
  const inertiaCount = auth?.user?.unread_notifications_count ?? 0
  const prevInertiaCount = useRef(inertiaCount)
  if (prevInertiaCount.current !== inertiaCount) {
    prevInertiaCount.current = inertiaCount
    setCount(inertiaCount)
  }

  useEffect(() => {
    if (!auth?.user?.id) return

    let cancelled = false

    // Immediate seed fetch so the badge is accurate on mount
    fetch(memberNotificationsUnreadCountPath(), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((res) => res.ok ? res.json() : null)
      .then((json) => { if (!cancelled && json) setCount(json.unread_count ?? 0) })
      .catch(() => {})

    // Real-time updates via ActionCable
    const consumer = createConsumer()
    const subscription = consumer.subscriptions.create("NotificationsChannel", {
      received(data: { unread_count: number }) {
        if (!cancelled) setCount(data.unread_count)
      },
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
      consumer.disconnect()
    }
  }, [auth?.user?.id])

  return count
}
